import React, { Fragment, useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  Typography,
} from '@mui/material';

interface VerticalViewModalProps {
  open: boolean;
  onClose: () => void;
  rowData: Record<string, any>;
  rowIndex?: number; // 0-based index from table
  onSave?: (updatedRow: Record<string, any>) => void | Promise<void>;
  onSaveError?: (err: Error) => void;
}

/**
 * Layout root causes (fixed here):
 * 1) Vertical overflow: Paper grew with field count; MUI vertical-centers it so the title
 *    leaves the viewport. Fix: maxHeight on Paper + overflow hidden; only DialogContent scrolls.
 * 2) Excessive width: fullWidth + width:'100%' let the paper stretch toward the viewport.
 *    Fix: explicit ~600px width (max 650px), no fullWidth / 100vw / width 100%.
 *
 * Global index.css applies translateX on .MuiDialog-paper for sidebar centering —
 * do not set transform: none (it fights that rule).
 */
export const verticalViewModalPaperSx = {
  m: 0,
  width: {
    xs: 'calc(100vw - 16px)',
    sm: 600,
  },
  maxWidth: {
    xs: 'calc(100vw - 16px)',
    sm: 600,
    md: 650,
  },
  minWidth: 0,
  height: 'auto',
  maxHeight: {
    xs: 'calc(100dvh - 16px)',
    sm: 'calc(100dvh - 32px)',
  },
  display: 'flex',
  flexDirection: 'column' as const,
  overflow: 'hidden',
  position: 'relative' as const,
  top: 'auto',
  left: 'auto',
  borderRadius: 2,
  boxSizing: 'border-box' as const,
};

/** Above Header (modal+5) and Sidebar (modal+1); matches other app dialogs. */
const dialogAboveHeaderZIndex = (theme: { zIndex: { modal: number } }) => theme.zIndex.modal + 6;

const VerticalViewModal: React.FC<VerticalViewModalProps> = ({
  open,
  onClose,
  rowData,
  rowIndex,
  onSave,
  onSaveError,
}) => {
  const [editRow, setEditRow] = useState<Record<string, any>>({ ...rowData });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const titleRef = useRef<HTMLHeadingElement | null>(null);
  const selectedDataSetId = typeof rowIndex === 'number' ? rowIndex : rowData?.id ?? null;

  useEffect(() => {
    setEditRow({ ...rowData });
    setSaveError(null);
  }, [rowData, open]);

  // Reset body scroll and focus title whenever the modal opens or the selected row changes.
  useEffect(() => {
    if (!open) return;

    requestAnimationFrame(() => {
      contentRef.current?.scrollTo({
        top: 0,
        left: 0,
        behavior: 'auto',
      });
      titleRef.current?.focus({ preventScroll: true });
    });
  }, [open, selectedDataSetId]);

  const handleChange = (key: string, value: string) => {
    setEditRow((prev) => ({ ...prev, [key]: value }));
    setSaveError(null);
  };

  const handleSave = async () => {
    if (!onSave) {
      onClose();
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      await onSave(editRow);
      onClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save';
      setSaveError(message);
      onSaveError?.(err instanceof Error ? err : new Error(message));
    } finally {
      setSaving(false);
    }
  };

  const fieldEntries = Object.entries(editRow).filter(
    ([key]) =>
      !['id', 'ID', 'dataSetId', 'DataSetId', '_savedDataSetId', '_savedDataSetIndex'].includes(key)
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      scroll="paper"
      disableAutoFocus
      disableRestoreFocus
      disableEnforceFocus
      aria-labelledby="data-set-details-title"
      sx={{
        zIndex: dialogAboveHeaderZIndex,
        '& .MuiDialog-container': {
          alignItems: 'center',
          justifyContent: 'center',
          p: {
            xs: 1,
            sm: 2,
          },
        },
      }}
      PaperProps={{
        className: 'data-set-details-dialog',
        sx: verticalViewModalPaperSx,
      }}
    >
      <DialogTitle
        id="data-set-details-title"
        tabIndex={-1}
        ref={titleRef}
        sx={{
          flex: '0 0 auto',
          flexShrink: 0,
          px: {
            xs: 2,
            sm: 3,
          },
          py: 2,
          fontWeight: 700,
          fontSize: 18,
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
          zIndex: 2,
        }}
      >
        Data Set Details
      </DialogTitle>

      <DialogContent
        ref={contentRef}
        dividers={false}
        sx={{
          flex: '1 1 auto',
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          px: {
            xs: 2,
            sm: 3,
          },
          py: 2,
          WebkitOverflowScrolling: 'touch',
          touchAction: 'pan-y',
          overscrollBehavior: 'contain',
          boxSizing: 'border-box',
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: '170px minmax(0, 1fr)',
            },
            columnGap: 2,
            rowGap: 1.5,
            alignItems: 'center',
            width: '100%',
            minWidth: 0,
          }}
        >
          {typeof rowIndex === 'number' && (
            <Fragment>
              <Typography
                sx={{
                  fontWeight: 600,
                  color: 'text.secondary',
                  overflowWrap: 'anywhere',
                }}
              >
                Data Set ID
              </Typography>
              <Typography sx={{ fontWeight: 600, minWidth: 0 }}>
                {rowIndex + 1}
              </Typography>
            </Fragment>
          )}

          {fieldEntries.map(([key, value]) => (
            <Fragment key={key}>
              <Typography
                title={key}
                sx={{
                  minWidth: 0,
                  overflowWrap: 'anywhere',
                  wordBreak: 'break-word',
                  fontSize: 14,
                }}
              >
                {key}
              </Typography>
              <TextField
                value={value ?? ''}
                onChange={(e) => handleChange(key, e.target.value)}
                size="small"
                variant="outlined"
                fullWidth
                inputProps={{
                  'aria-label': key,
                  style: { fontSize: 14, padding: '6px 8px' },
                }}
                sx={{
                  minWidth: 0,
                  background: 'white',
                  '& .MuiInputBase-root': {
                    minWidth: 0,
                  },
                  '& input': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  },
                }}
              />
            </Fragment>
          ))}
        </Box>

        {saveError && (
          <Typography color="error" variant="body2" sx={{ pt: 1.5 }}>
            {saveError}
          </Typography>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          flex: '0 0 auto',
          flexShrink: 0,
          justifyContent: 'flex-end',
          gap: 1,
          width: '100%',
          px: {
            xs: 2,
            sm: 3,
          },
          py: 2,
          bgcolor: 'background.paper',
          borderTop: '1px solid',
          borderColor: 'divider',
          zIndex: 2,
          boxSizing: 'border-box',
          overflow: 'visible',
        }}
      >
        <Button onClick={onClose} variant="outlined" color="inherit" disabled={saving}>
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" color="primary" disabled={saving}>
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VerticalViewModal;
